import "@assets/css/bootstrap-grid.css";
import { EyeClosedIcon, KeyIcon } from "@assets/icons";
import { Checkbox } from "@components/ui/checkbox";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS, SelectProps } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  TeamMemberSchema,
  TeamMembersFormData,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { RoleData, TeamUserData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import { Eye, SearchIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router";

const TeamMemberForm: React.FC = () => {
  const { searchParams, handleAction } = useHandleAction({});
  const { type } = useParams();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const modalItem = searchParams.get("modal-item");
  const modalMode = searchParams.get("modal");

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<TeamMembersFormData>({
    mode: "onChange",
    resolver: zodResolver(TeamMemberSchema),
  });

  console.log("Error", errors);

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<TeamUserData>
  >({
    endpoint: endpoint["team-member"],
    key: `${pathname}-${modalItem}`,
    enabled: modalItem ? true : false,
    id: modalItem || "",
    window: false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      console.log("firstResult", firstResult);
      if (firstResult) {
        if (modalMode == "add") {
          return reset({});
        } else {
          reset(() => ({
            is_masterclass: firstResult?.is_masterclass,
            first_name: firstResult.first_name,
            last_name: firstResult.last_name,
            designation: [
              {
                label: firstResult.designation?.name,
                value: firstResult.designation?.id,
              },
            ],
            email: firstResult.email,
            image: firstResult.image,
            role: firstResult.role?.map((item: RoleData) => ({
              label: item.name,
              value: item.id,
            })),
            is_superuser: firstResult.is_superuser,
            bio: firstResult?.bio,
          }));
        }
      }
    }
  }, [fetchData?.data, fetchPending]);

  const handleMutate = (data: any) => {
    data = {
      ...data,
      role: data["role"]?.map((item: SelectProps) => item.value),
      designation: data["designation"]?.[0]?.["value"],
    };
    if (modalItem) {
      data = {
        ...data,
        id: modalItem,
      };
      // if (!(data.image instanceof File || data.image instanceof Blob))
      //   delete data.image;
    }
    return {
      data,
      endpoint: endpoint["team-member"],
      method: modalItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({
    status,
  }: {
    data: TeamUserData;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      showToast({
        description: `Team member ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "modal-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const { setIsFormPending } = useSelectionStore();
  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
  };

  const { mutate, isPending } = usePostData();
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      if (modalMode == "add") {
        if (!watch("password")) {
          setError("password", { message: "password is required" });
        }
        if (!watch("confirm_password")) {
          setError("confirm_password", {
            message: "confirm password is required",
          });
        }
        return;
      }
    }
  }, [watch("confirm_password"), watch("password"), modalMode, errors]);
  const onSubmit = (data: TeamMembersFormData) => {
    console.log("data", data);
    if (modalMode == "add") {
      if (!watch("password")) {
        setError("password", { message: "password is required" });
        return;
      }
      if (!watch("confirm_password")) {
        setError("confirm_password", {
          message: "confirm password is required",
        });
        return;
      }
    }

    clearErrors(["confirm_password", "password"]);
    //Add Mutation Data
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirm_password: false,
  });
  const formSchema: FormField[] = [
    {
      name: "is_superuser",
      label: "Superadmin Access",
      colSpan: 12,
      type: "checkbox",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] flex justify-end bg-[red] min-w-full w-full`,
      visable: true,
    },
    {
      name: "first_name",
      label: "First Name",
      colSpan: 6,
      type: "text",
      placeholder: "Enter First Name",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "last_name",
      label: "Last Name",
      colSpan: 6,
      type: "text",
      placeholder: "Enter Last Name",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "designation",
      label: "Designation",
      colSpan: 6,
      type: "async",
      endpoint: endpoint["designation"],
      format: { key: "name", value: "id", mutate: "name" },
      settings: {
        add: true,
        debounce: 300,
        remove: true,
        update: true,
      },
      endIcon: <SearchIcon className="text-[#ABABAB]" />,
      placeholder: "Search Designation",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      label: "",
      type: "component",
      component: (
        <div className="col-span-12 flex gap-2 items-center">
          <label htmlFor="masterclass" className="cursor-pointer">
            {" "}
            <p className="text-[14px] font-semibold">
              Include in Skillshikshya Masterclass Team?
            </p>
            <span className="text-[14px] text-[#ABABAB]">
              If enabled, this user will be officially recognized as a
              Skillshikshya Masterclass team member.
            </span>
          </label>
          <Controller
            name="is_masterclass"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="masterclass"
                checked={!!field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
        </div>
      ),
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "role",
      label: "Manage Role",
      colSpan: 6,
      type: "async",
      endpoint: endpoint["groups"],
      format: { key: "name", value: "id" },
      isMulti: true,
      placeholder: "Manage Role",
      control: control,
      endIcon: <KeyIcon />,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "email",
      label: "Email Address",
      colSpan: 12,
      type: "email",
      placeholder: "Enter Email Address",
      control: control,

      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "password",
      label: "Password",
      colSpan: 6,
      type: `${showPassword.password ? "text" : "password"}`,
      placeholder: "***************",
      control: control,
      endIcon: (
        <>
          {showPassword.password ? (
            <Eye
              size={17}
              color="#ABABAB"
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  password: !prevState.password,
                }))
              }
            />
          ) : (
            <span
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  password: !prevState.password,
                }))
              }
            >
              <EyeClosedIcon />
            </span>
          )}
        </>
      ),
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "confirm_password",
      label: "Confirm Password",
      colSpan: 6,
      type: `${showPassword.confirm_password ? "text" : "password"}`,
      placeholder: "***************",
      control: control,
      endIcon: (
        <>
          {showPassword.confirm_password ? (
            <Eye
              size={17}
              color="#ABABAB"
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  confirm_password: !prevState.confirm_password,
                }))
              }
            />
          ) : (
            <span
              onClick={() =>
                setShowPassword((prevState) => ({
                  ...prevState,
                  confirm_password: !prevState.confirm_password,
                }))
              }
            >
              <EyeClosedIcon />
            </span>
          )}
        </>
      ),
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "image",
      label: "Image",
      colSpan: 12,
      type: "cropper",
      placeholder: "Enter Email Address",
      control: control,
      endIcon: <KeyIcon />,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "bio",
      label: "Bio",
      colSpan: 12,
      type: "textarea",
      placeholder: "Write the Bio for the team member",
      control: control,
      endIcon: <KeyIcon />,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      type: "submit",
      value: `${searchParams.get("modal") || ""} ${(type || pathname)?.replace(
        "-",
        " "
      )}`,
      className: "w-full h-[47px] mt-[32px] capitalize",
      colSpan: 12,
      variant: "default",
      endIcon: isPending ? <Spinner /> : null,
      disabled: isPending ? true : false,
      visable: true,
    },
  ];

  if (fetchPending && modalItem)
    return (
      <>
        <SkeletonTwo />
      </>
    );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
        {formSchema.map(
          (field, index: number) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default TeamMemberForm;
